import { Link, useLocation } from "react-router-dom";
import { Heart } from "lucide-react";
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import authService from '../../../Services/authService';
import { useCart } from "../../../Services/CartContext";
import { useNavigate } from "react-router-dom";
import QuantityControlButton from "../Order/QuantityControlButton";
import SEOSchema from '../../../utils/SEOSchema';

export default function CategoryPage() {
  const { fetchCartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  let testData = location.pathname.split('/')[1]; // Use index 1
  let categoryParts = testData.split('-');
  const [categoryData, setCategoryData] = useState([]);
  const [cartState, setCartState] = useState({});
  const [cartItems, setCartItems] = useState([]);

  let categoryName = categoryParts.join(' ');

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top when page loads
  }, []);

  useEffect(() => {
    loadTestsbyCategory();
    const user = authService.getCurrentuser();
    if (user) {
      loadCartItems();
    }
  }, []);

  const loadCartItems = () => {
    const token = authService.getCurrentUsertoken();
    const url = new URL(authService.getApiUrl() + 'cartlist');
    fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        user_id: authService.getCurrentuser().id,
        organization_id: 1
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (res.status === 401) {
          authService.clientlogout();
          window.location.href = "/login";
        } else {
          return res.json();
        }
      })
      .then((result) => {
        const cartItems = result.data;
        setCartItems(cartItems);
      })
      .catch((err) => {
        console.log(err.message);
      });
  };


  const loadTestsbyCategory = () => {
    const url = new URL(authService.getApiUrl() + 'labtestsbycategory');
    fetch(url, {
      method: 'POST',
      body: JSON.stringify({ category_name: categoryName }),
      headers: { Accept: 'application/json', 'Content-Type': 'application/json; charset=UTF-8' },
    })
      .then((res) => res.json())
      .then((result) => setCategoryData(result.data))
      .catch((err) => console.log(err.message));
  };

  const handleAdd = (test) => {
    const user = authService.getCurrentuser();
    if (!user) {
      navigate("/login");
      return;
    }

    const token = authService.getCurrentUsertoken();
    fetch(authService.getApiUrl() + "cart", {
      method: "POST",
      body: JSON.stringify({
        user_id: user.id,
        type: "product",
        name: test.test_name,
        test_id: test.id,
        quantity: 1,
        price: test.price,
        overall_price: test.price,
        preparation: "",
        organization_id: 1,
        visit_type: test.visit_type,
      }),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=UTF-8",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then(() => {
        setCartState(prev => ({
          ...prev,
          [test.id]: { isAdded: true, quantity: 1 }
        }));
        fetchCartCount();
      })
      .catch((err) => console.error("Error adding item:", err));
  };

  const getQuantityForTest = (testId) => {
    const foundItem = cartItems.find((item) => item.test_id === testId);
    return foundItem ? foundItem.quantity : 0;
  };

  return (
    <>
      <Helmet>
        <title>{categoryName} Tests - Pranaam365</title>
        <meta name="description" content={`Book ${categoryName.toLowerCase()} tests online. Comprehensive ${categoryName.toLowerCase()} screening and health checkups.`} />
        <meta name="keywords" content={`${categoryName.toLowerCase()}, ${categoryName.toLowerCase()} tests, lab tests, diagnostic tests, health screening, Pranaam365`} />
      </Helmet>
      
      <SEOSchema 
        type="webpage"
        data={{
          title: `${categoryName} Tests - Pranaam365`,
          description: `Comprehensive ${categoryName.toLowerCase()} screening tests and health checkups`,
          url: window.location.href
        }}
      />
    
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Navigation */}
      <div className="bg-white border-b border-[#E0E0E0]">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-2">
          <div className="flex items-center space-x-2">
            <Link to="/" className="text-xs text-[#4A4A4A]/70 hover:text-[#951C25]">Home</Link>
            <span className="text-[#4A4A4A]/30">/</span>
            <Link to="#" className="text-xs text-[#4A4A4A]/70 hover:text-[#951C25]">{categoryName}</Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-[rgb(149,28,37,0.2)] border-b border-[#E0E0E0]">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-white/50 backdrop-blur-sm flex items-center justify-center">
              <Heart size={32} className="text-[#951C25]" strokeWidth={1.5} fill="none" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-[#951C25] mb-0.5">{categoryName}</h1>
              <p className="text-xs text-[#951C25]/80 max-w-2xl">
                Comprehensive {categoryName.toLowerCase()} screening tests to monitor your {categoryName.toLowerCase()} health.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Individual Tests Section */}
      <section className="py-10 md:py-14 bg-[#F5F5F7]">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#4A4A4A] mb-8">
            Individual <span className="text-[#951C25]">{categoryName}</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {categoryData.map((test, index) => (
              <div key={index} className="bg-white p-4 rounded-lg hover:shadow-sm transition-shadow border border-[#E0E0E0] hover:border-[#951C25]/30">
                <Link to={'/' + encodeURIComponent(categoryName.replace(/ /g, '-')) + '/' + encodeURIComponent(test.test_name.replace(/ /g, '-')) + '-' + test.id} className="block mb-3">
                  <h3 className="text-base font-medium text-[#4A4A4A] mb-2 line-clamp-2 min-h-[3rem]">{test.test_name}</h3>
                  <p className="text-xs text-[#4A4A4A]/70 line-clamp-3 min-h-[2.5rem]">{test.short_description}</p>
                </Link>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#951C25]">₹{test.price} <span className="line-through text-gray-500 ml-1">₹{test.mrp_price}</span></span>
                  <div className="flex items-center justify-between">
                    <QuantityControlButton
                      id={test.id}
                      name={test.test_name}
                      price={test.price}
                      variant="simple"
                      className="text-xs"
                      visit_type={test.visit_type}
                      quantity={getQuantityForTest(test.id)} // Make sure to provide default 0 for quantity
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
